import { SearchIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DateRangeFilter, {
	formatDayRangeLabel,
} from "@/components/admin-portal/appointment/date-range-filter";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { useDateContext } from "@/components/providers/date-provider";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn, formatCurrency } from "@/lib/utils";
import type {
	IncentivePayout,
	PayoutStatus,
} from "@/types/admin-portal/bonus-ro";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import {
	actorLabel,
	DEFAULT_LIMIT,
	formatTime,
	limitParam,
	pageParam,
	visit,
} from "./balances";
import { type ActiveFilter, FilterPanel } from "./filter-panel";
import { LoadFailed, Loading } from "./load-state";
import { PayoutActionDialog } from "./payout-action-dialog";
import { PAYOUT_ACTIONS, PAYOUT_STATUSES, type PayoutAction } from "./payouts";
import { formatDay } from "./rules";
import { TabNote } from "./tab-note";

export interface PayoutsTabProps {
	payouts?: ExternalProp<IncentivePayout[]>;
	/** the status filter; null lists every status */
	payoutStatus: PayoutStatus | null;
	payoutFilters: PayoutFilters;
	routes: { payoutTransition: string };
}

const ALL = "all";

const STATUS_BADGE: Record<PayoutStatus, BadgeProps["variant"]> = {
	DRAFT: "outline",
	APPROVED: "accent",
	PAID: "success",
	CANCELLED: "secondary",
};

const ACTION_VARIANT: Record<PayoutAction, "default" | "outline"> = {
	approve: "default",
	paid: "default",
	cancel: "outline",
};

function formatPeriod({
	start,
	end,
}: {
	start?: string | null;
	end?: string | null;
}) {
	if (!start && !end) return null;
	return `${start ? formatDay(start) : "…"} – ${end ? formatDay(end) : "…"}`;
}

/** The three admin steps every payout walks; a step that has not happened has no time. */
function stepsOf(payout: IncentivePayout) {
	return [
		{
			key: "created",
			by: payout.createdBy,
			name: payout.createdByName,
			at: payout.createdAt,
		},
		{
			key: "approved",
			by: payout.approvedBy,
			name: payout.approvedByName,
			at: payout.approvedAt,
		},
		{
			key: "paid",
			by: payout.paidBy,
			name: payout.paidByName,
			at: payout.paidAt,
		},
	] as const;
}

/**
 * Created, approved, paid as a trail. A cancelled payout stops at its first missing step,
 * which shows who cancelled it, when, and why.
 */
function StepTrail({ payout }: { payout: IncentivePayout }) {
	const { t } = useTranslation("bonus-ro");
	const steps = stepsOf(payout);
	const stoppedAt =
		payout.status === "CANCELLED" ? steps.findIndex((step) => !step.at) : -1;

	return (
		<ol className="grid grid-cols-3">
			{steps.map((step, i) => {
				const stopped = i === stoppedAt;
				const shown = stopped
					? {
							by: payout.cancelledBy,
							name: payout.cancelledByName,
							at: payout.cancelledAt,
						}
					: step;
				const done = !!step.at;
				const next = steps[i + 1];
				return (
					<li key={step.key} className="min-w-0 pr-3">
						<div aria-hidden className="flex items-center gap-2">
							<span
								className={cn(
									"size-2.5 shrink-0 rounded-full",
									done ? "bg-primary" : stopped ? "bg-secondary" : "bg-border",
								)}
							/>
							{next && (
								<span
									className={cn(
										"h-px flex-1",
										next.at ? "bg-primary" : "bg-border",
									)}
								/>
							)}
						</div>
						<p
							className={cn(
								"mt-1.5 text-xs font-medium",
								stopped ? "text-secondary" : !done && "text-muted-foreground",
							)}
						>
							{stopped
								? t("payouts.statuses.CANCELLED")
								: t(`payouts.steps.${step.key}`)}
						</p>
						<p className="text-xs leading-snug text-muted-foreground">
							{shown.at ? actorLabel(shown.by, shown.name) : "—"}
							{shown.at && (
								<span className="block tabular-nums">
									{formatTime(shown.at)}
								</span>
							)}
						</p>
						{stopped && payout.cancelReason && (
							<p className="mt-1 break-words text-xs">
								{t("payouts.cancel_reason")}{" "}
								<span className="font-medium">{payout.cancelReason}</span>
							</p>
						)}
						{step.key === "paid" && payout.reference && (
							<p className="mt-1 text-xs">
								{t("payouts.reference")}{" "}
								<span className="font-mono font-medium">
									{payout.reference}
								</span>
							</p>
						)}
					</li>
				);
			})}
		</ol>
	);
}

const TEXT_FILTERS = [
	"search",
	"creator",
	"approver",
	"amount_min",
	"amount_max",
] as const;
type TextFilter = (typeof TEXT_FILTERS)[number];
const DAY_RANGES = ["created", "approved", "paid"] as const;
type DayRange = (typeof DAY_RANGES)[number];
/** as the controller forwards them: amounts arrive as numbers, days as YYYY-MM-DD in WIB */
type PayoutFilters = Partial<
	Record<TextFilter | `${DayRange}_${"from" | "to"}`, string | number>
>;

const textOf = (filters: PayoutFilters) =>
	Object.fromEntries(
		TEXT_FILTERS.map((key) => [key, String(filters[key] ?? "")]),
	) as Record<TextFilter, string>;

// Digits without leading zeros, so a typed amount reads back from the server unchanged;
// otherwise the debounce would see a difference after every visit and refetch forever.
const asAmount = (value: string) =>
	value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

export function PayoutsTab({
	payouts,
	payoutStatus,
	payoutFilters,
	routes,
}: PayoutsTabProps) {
	const { t } = useTranslation("bonus-ro");
	const { t: tAppointments } = useTranslation("appointments");
	const { locale } = useDateContext();
	const applied = textOf(payoutFilters);
	const [drafts, setDrafts] = useState(applied);
	const debounced = useDebouncedValue(drafts, 400);
	const setDraft = (key: TextFilter, value: string) =>
		setDrafts((prev) => ({ ...prev, [key]: value }));
	const [acting, setActing] = useState<{
		payout: IncentivePayout;
		action: PayoutAction;
	} | null>(null);

	const rows = payouts?.data ?? [];
	const page = payouts?.meta?.page ?? 1;
	const limit = payouts?.meta?.limit ?? DEFAULT_LIMIT;
	const pages = payouts?.meta?.totalPages ?? 1;
	const from = (page - 1) * limit + 1;
	const goTo = (nextPage: number, nextLimit = limit) =>
		visit({ page: pageParam(nextPage), limit: limitParam(nextLimit) }, [
			"payouts",
		]);

	const applyFilters = (patch: Record<string, string | undefined>) =>
		visit({ ...patch, page: undefined }, [
			"payouts",
			"payoutStatus",
			"payoutFilters",
		]);

	useEffect(() => {
		const current = textOf(payoutFilters);
		const patch = Object.fromEntries(
			TEXT_FILTERS.filter(
				(key) => debounced[key].trim() !== current[key].trim(),
			).map((key) => [key, debounced[key].trim() || undefined]),
		);
		if (Object.keys(patch).length === 0) return;
		visit({ ...patch, page: undefined }, ["payouts", "payoutFilters"]);
	}, [debounced, payoutFilters]);

	const day = (range: DayRange, end: "from" | "to") =>
		String(payoutFilters[`${range}_${end}`] ?? "");
	const textChip = (key: "search" | "creator" | "approver") =>
		applied[key].trim()
			? [
					{
						key,
						label: t(`payouts.filters.${key}`),
						value: applied[key].trim(),
						clear: () => setDraft(key, ""),
					},
				]
			: [];
	const amountLabel = () => {
		const [min, max] = [applied.amount_min, applied.amount_max].map(
			(value) => value && formatCurrency(Number(value)),
		);
		if (min && max) return `${min} - ${max}`;
		return min
			? t("payouts.filters.amount_from", { amount: min })
			: t("payouts.filters.amount_to", { amount: max });
	};
	const activeFilters: ActiveFilter[] = [
		...textChip("search"),
		...(payoutStatus
			? [
					{
						key: "status",
						label: t("payouts.status"),
						value: t(`payouts.statuses.${payoutStatus}`),
						clear: () => applyFilters({ status: undefined }),
					},
				]
			: []),
		...(applied.amount_min || applied.amount_max
			? [
					{
						key: "amount",
						label: t("payouts.filters.amount"),
						value: amountLabel(),
						clear: () =>
							setDrafts((prev) => ({
								...prev,
								amount_min: "",
								amount_max: "",
							})),
					},
				]
			: []),
		...textChip("creator"),
		...textChip("approver"),
		...DAY_RANGES.flatMap((range) =>
			day(range, "from") || day(range, "to")
				? [
						{
							key: range,
							label: t(`payouts.filters.${range}_range`),
							value: formatDayRangeLabel(
								day(range, "from"),
								day(range, "to"),
								tAppointments,
								locale,
							),
							clear: () =>
								applyFilters({
									[`${range}_from`]: undefined,
									[`${range}_to`]: undefined,
								}),
						},
					]
				: [],
		),
	];

	function clearFilters() {
		setDrafts(textOf({}));
		const set = [
			...(payoutStatus ? ["status"] : []),
			...DAY_RANGES.flatMap((range) => [`${range}_from`, `${range}_to`]).filter(
				(key) => payoutFilters[key as keyof PayoutFilters],
			),
		];
		if (set.length > 0)
			applyFilters(Object.fromEntries(set.map((key) => [key, undefined])));
	}

	const textField = (
		key: "search" | "creator" | "approver",
		className?: string,
	) => (
		<div className={cn("grid gap-2", className)}>
			<Label className="text-xs text-muted-foreground">
				{t(`payouts.filters.${key}`)}
			</Label>
			<Input
				className="bg-input shadow-inner"
				value={drafts[key]}
				placeholder={t(`payouts.filters.${key}_placeholder`)}
				aria-label={t(`payouts.filters.${key}`)}
				StartIcon={{ icon: SearchIcon }}
				isLoading={debounced[key] !== drafts[key]}
				EndIcon={
					drafts[key]
						? {
								icon: XIcon,
								isButton: true,
								handleOnClick: () => setDraft(key, ""),
							}
						: undefined
				}
				onChange={(e) => setDraft(key, e.target.value)}
			/>
		</div>
	);
	const dayField = (range: DayRange) => (
		<div className="grid gap-2">
			<Label className="text-xs text-muted-foreground">
				{t(`payouts.filters.${range}_range`)}
			</Label>
			<DateRangeFilter
				value={{ from: day(range, "from"), to: day(range, "to") }}
				onChange={(next) =>
					applyFilters({
						[`${range}_from`]: next.from || undefined,
						[`${range}_to`]: next.to || undefined,
					})
				}
				placeholder={t(`payouts.filters.${range}_range`)}
			/>
		</div>
	);

	return (
		<div className="flex flex-col gap-4">
			<TabNote>{t("payouts.description")}</TabNote>

			<FilterPanel
				activeFilters={activeFilters}
				canClear={
					TEXT_FILTERS.some((key) => drafts[key]) || activeFilters.length > 0
				}
				onClearAll={clearFilters}
			>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-3 lg:grid-cols-4">
					{textField("search", "md:col-span-2")}
					<div className="grid gap-2">
						<Label className="text-xs text-muted-foreground">
							{t("payouts.status")}
						</Label>
						<Select
							value={payoutStatus ?? ALL}
							onValueChange={(value) =>
								applyFilters({ status: value === ALL ? undefined : value })
							}
						>
							<SelectTrigger
								className="bg-input"
								aria-label={t("payouts.filter")}
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={ALL}>{t("payouts.all_statuses")}</SelectItem>
								{PAYOUT_STATUSES.map((status) => (
									<SelectItem key={status} value={status}>
										{t(`payouts.statuses.${status}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="grid gap-2">
						<Label className="text-xs text-muted-foreground">
							{t("payouts.filters.amount")}
						</Label>
						<div className="grid grid-cols-2 gap-2">
							{(["amount_min", "amount_max"] as const).map((key) => (
								<Input
									key={key}
									className="bg-input shadow-inner tabular-nums"
									inputMode="numeric"
									maxLength={12}
									value={drafts[key]}
									placeholder={t(`payouts.filters.${key}`)}
									aria-label={t(`payouts.filters.${key}`)}
									isLoading={debounced[key] !== drafts[key]}
									onChange={(e) => setDraft(key, asAmount(e.target.value))}
								/>
							))}
						</div>
					</div>
					{textField("creator")}
					{dayField("created")}
					{textField("approver")}
					{dayField("approved")}
					{dayField("paid")}
				</div>
			</FilterPanel>

			{!payouts ? (
				<Loading />
			) : payouts.error ? (
				<LoadFailed
					title={t("failed.payouts")}
					error={payouts.error}
					only="payouts"
				/>
			) : rows.length === 0 ? (
				<div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
					{activeFilters.length > 0
						? t("payouts.no_match")
						: t("payouts.empty")}
					{page > 1 && (
						<Button variant="outline" size="sm" onClick={() => goTo(1)}>
							{t("therapists.first_page")}
						</Button>
					)}
				</div>
			) : (
				<>
					<ul className="flex flex-col gap-2">
						{rows.map((row) => {
							const period = formatPeriod({
								start: row.periodStart,
								end: row.periodEnd,
							});
							return (
								<li
									key={row.id}
									className="grid gap-4 rounded-lg border bg-card p-4 md:grid-cols-[minmax(0,13rem)_minmax(0,1fr)_14rem] md:items-center md:gap-6"
								>
									<div className="min-w-0">
										<p className="text-sm font-medium">
											{row.therapistName ?? row.therapistId.slice(0, 8)}
										</p>
										<p className="text-lg font-semibold tabular-nums">
											{formatCurrency(row.amount)}
										</p>
										<Badge variant={STATUS_BADGE[row.status]} className="mt-1">
											{t(`payouts.statuses.${row.status}`, {
												defaultValue: row.status,
											})}
										</Badge>
									</div>
									<div className="min-w-0">
										<StepTrail payout={row} />
										{(period || row.notes) && (
											<p className="mt-2 whitespace-pre-wrap break-words text-xs text-muted-foreground">
												{period && `${t("payouts.period")} ${period}`}
												{period && row.notes && " · "}
												{row.notes}
											</p>
										)}
									</div>
									<div className="flex flex-wrap gap-2 md:justify-end">
										{(PAYOUT_ACTIONS[row.status] ?? []).map((action) => (
											<Button
												key={action}
												variant={ACTION_VARIANT[action]}
												size="sm"
												onClick={() => setActing({ payout: row, action })}
											>
												{t(`payout_actions.${action}.label`)}
											</Button>
										))}
									</div>
								</li>
							);
						})}
					</ul>
					<ApptPagination
						metadata={{
							page,
							pages,
							limit,
							count: payouts.meta?.totalItems ?? rows.length,
							from,
							to: from + rows.length - 1,
						}}
						label={t("payouts.pagination_label")}
						actions={{
							goToPrevpage: () => goTo(page - 1),
							goToNextPage: () => goTo(page + 1),
							goToPage: (value) => goTo(Number(value)),
							goToFirstPage: () => goTo(1),
							goToLastPage: () => goTo(pages),
							onChangeLimit: (value) => goTo(1, Number(value)),
						}}
					/>
				</>
			)}

			{acting && (
				<PayoutActionDialog
					payout={acting.payout}
					action={acting.action}
					urlTemplate={routes.payoutTransition}
					onClose={() => setActing(null)}
				/>
			)}
		</div>
	);
}
